import { z } from 'zod';
/**
 * FeedbackEvent — the wire shape every micro-feedback signal carries
 * (issue #166 PR A — Phase 2 trust loop). Six event kinds drive the
 * feedback primitives the portal renders:
 *
 *   FB-01 line-vote      — end-of-line up/down on a brief item
 *   FB-02 selection      — text-selection floating bar ("wrong" / cite-as-bad)
 *   FB-03 microbar       — end-of-read 3-button row (useful / meh / wrong)
 *                          dismiss 3× → suppress for a week per artifact kind
 *   FB-04 self-doubt     — agent volunteers low-confidence prompt
 *                          ("Wasn't sure on this one — was I right?")
 *   FB-05 outcome        — prediction-outcome follow-back (one per session)
 *   FB-06 correction-ack — user acks an agent correction
 *                          (propagation choice carried in `body`)
 *
 * Every event is org-scoped and actor-stamped so the calibration layer
 * can roll accuracy + recent-misses per org. Persisted under
 * `organizations/{orgId}/feedback/{id}` via the backend store; the
 * audit ledger gets a parallel FEEDBACK.* row.
 */
/** Closed set of feedback event kinds (FB-01 … FB-06). */
export const FeedbackEventKind = z.enum([
    'line-vote',
    'selection',
    'microbar',
    'self-doubt',
    'outcome',
    'correction-ack',
]);
/**
 * FeedbackTargetRef — what the feedback is about. `spanStart` / `spanEnd`
 * are only populated for FB-02 (selection floating-bar) where the user
 * highlighted a sub-range of synthesized text.
 */
export const FeedbackTargetRef = z.object({
    kind: z.enum(['brief', 'brief-item', 'dossier', 'dossier-fact', 'ask-message', 'memory-row']),
    id: z.string(),
    spanStart: z.number().int().nonnegative().optional(),
    spanEnd: z.number().int().nonnegative().optional(),
});
/**
 * FeedbackSentiment — closed set of sentiment values across all six
 * feedback kinds. Different kinds use different subsets (e.g. FB-01
 * line-vote uses positive/negative; FB-03 microbar uses useful/meh/wrong;
 * FB-06 correction-ack uses confirm/partial-confirm). Routing logic
 * lives server-side in feedback-store; the schema is permissive about
 * which sentiment pairs with which kind so the wire format stays small.
 */
export const FeedbackSentiment = z.enum([
    'positive',
    'negative',
    'neutral',
    'wrong',
    'confirm',
    'noise',
    'meh',
    'useful',
    'partial-confirm',
]);
/**
 * FeedbackEvent — one micro-feedback row. `body` carries free-text
 * payloads (FB-02 "what's wrong" Comment, FB-06 propagation choice).
 */
export const FeedbackEvent = z.object({
    id: z.string(),
    orgId: z.string(),
    actorUid: z.string(),
    kind: FeedbackEventKind,
    target: FeedbackTargetRef,
    sentiment: FeedbackSentiment,
    body: z.string().max(2000).optional(),
    createdAt: z.string().datetime(),
});
